type MessageToBase64Converter<T> = (input: T) => string | null
type ErrorConverter<T> = (input: T) => string | undefined

export type DownloadProgress = {
  downloaded: number
  totalSize: number
}
export type ProgressListener = (input: DownloadProgress) => void
export type Config<T> = {
  url: string
  fileName: string
  abortController: AbortController

  /**
   * Extracts the base64 encoded byes from the message stream.
   * The bytes will be decoded and written to the file
   */
  converter: MessageToBase64Converter<T>

  /**
   * Extracts the error from the response
   * In case the error it's embedded in the response
   */
  errorConverter?: ErrorConverter<T>
  /**
   * Allows listening for the download progress
   */
  progressListener?: ProgressListener
}
type ConfigWithErrorConverter<T> = Omit<Config<T>, 'errorConverter'> & {
  errorConverter: ErrorConverter<T>
}

export function useStreamingDownload() {
  const securityStore = useSecurityStore()
  const { apiToken } = storeToRefs(securityStore)
  const adapter = computed(() => {
    if (window.showSaveFilePicker !== undefined) {
      return fileSystemApiAdapter()
    }
    if (navigator.serviceWorker.controller) {
      return serviceWorkerAdapter()
    }
    return inMemoryAdapter()
  })
  const isAvailable = computed(() => adapter.value !== null)

  function download<T>(config: Config<T>) {
    const errorConverter: ErrorConverter<T> = config.errorConverter || ((_input: T) => undefined)
    return adapter.value.startDownload(apiToken.value, { ...config, errorConverter })
  }

  return {
    isAvailable,
    download,
  }
}
/**
 * Basic in memory download, file dialog it's triggered via a.href.click()
 */
function inMemoryAdapter() {
  return {
    startDownload: async function <T>(apiToken: string, config: ConfigWithErrorConverter<T>) {
      console.log('Using in memory adapter')
      const response = await fetch(config.url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
        signal: config.abortController.signal,
      })
      if (response.status !== 200) {
        throw new Error(response.statusText)
      }

      const detectedSize = parseInt(response.headers.get('Content-Length') || '0')
      const contentType = (response.headers.get('content-type') || '').toLowerCase()
      let responseBody = response.body
      if (!responseBody) {
        throw new Error('Empty response body')
      }
      if (contentType.includes('application/octet-stream')) { // raw byte chunks
        responseBody = responseBody
          .pipeThrough(new ProgressEmittingByteStreamProcessor(detectedSize, config.progressListener))
      } else if (contentType.includes('application/x-ndjson')) { // base64 encoded json chunks
        responseBody = responseBody
          .pipeThrough(new Utf8DecodingStream())
          .pipeThrough(new NdjsonParseStream<T>())
          .pipeThrough(new Base64TpByteStream<T>(detectedSize, config.converter, config.errorConverter, config.progressListener))
      } else {
        throw new Error(`Unsupported content type: ${contentType || 'unknown'}`)
      }

      const reader = responseBody.getReader()
      const aggregator: Uint8Array<ArrayBufferLike>[] = []
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }
        aggregator.push(value)
      }
      const totalSize = aggregator.reduce((size, array) => size + array.length, 0)
      const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(totalSize)
      let index = 0
      aggregator.forEach((chunk) => {
        bytes.set(chunk, index)
        index += chunk.length
      })
      const downloadElement = document.createElement('a')
      const href = window.URL.createObjectURL(new Blob([bytes.buffer], { type: 'application/octet-stream' }))
      downloadElement.href = href
      downloadElement.download = config.fileName
      document.body.appendChild(downloadElement)
      downloadElement.click()
      document.body.removeChild(downloadElement)
      window.URL.revokeObjectURL(href)
    },
  }
}
/**
 * Saves the file be intercepting an iframe url via service worker and tricking the browser to show the save file dialog
 * @apiNotes - might timeout on big downloads as service worker it's put to sleep by browser
 */
function serviceWorkerAdapter() {
  return {
    startDownload: async function <T>(apiToken: string, config: ConfigWithErrorConverter<T>) {
      console.log('Using service worker adapter')
      const channel = new MessageChannel()
      const downloadId = crypto.randomUUID()
      navigator.serviceWorker.controller!.postMessage({
        type: 'REGISTER_DOWNLOAD',
        port: channel.port1,
        id: downloadId,
      }, [channel.port1])

      const downloadElement = document.createElement('iframe')
      downloadElement.style.display = 'none'
      downloadElement.src = `/proxy-download?download-id=${downloadId}&download-name=${config.fileName}`
      document.body.appendChild(downloadElement)
      try {
        const response = await fetch(config.url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${apiToken}`,
          },
          signal: config.abortController.signal,
        })
        if (response.status !== 200) {
          channel.port2.postMessage('end')
          document.body.removeChild(downloadElement)
          throw new Error(response.statusText)
        }

        const detectedSize = parseInt(response.headers.get('Content-Length') || '0')
        const contentType = (response.headers.get('content-type') || '').toLowerCase()
        let responseBody = response.body
        if (!responseBody) {
          throw new Error('Empty response body')
        }
        if (contentType.includes('application/octet-stream')) { // raw byte chunks
          responseBody = responseBody
            .pipeThrough(new ProgressEmittingByteStreamProcessor(detectedSize, config.progressListener))
        } else if (contentType.includes('application/x-ndjson')) { // base64 encoded json chunks
          responseBody = responseBody
            .pipeThrough(new Utf8DecodingStream())
            .pipeThrough(new NdjsonParseStream<T>())
            .pipeThrough(new Base64TpByteStream<T>(detectedSize, config.converter, config.errorConverter, config.progressListener))
        } else {
          throw new Error(`Unsupported content type: ${contentType || 'unknown'}`)
        }

        const reader = responseBody.getReader()
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            channel.port2.postMessage('end')
            document.body.removeChild(downloadElement)
            break
          }
          channel.port2.postMessage(value)
        }
      } catch (e) {
        channel.port2.postMessage('end')
        document.body.removeChild(downloadElement)
        throw e
      }
    },
  }
}
/**
 * Saves the file using the file system adapter(currently available only on chrome/edge)
 */
function fileSystemApiAdapter() {
  return {
    startDownload: async function <T>(apiToken: string, config: ConfigWithErrorConverter<T>) {
      console.log('Using file system adapter')
      const fileHandler = await window.showSaveFilePicker({
        suggestedName: config.fileName,
      })
      const writableStream = await fileHandler.createWritable()
      const response = await fetch(config.url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
        signal: config.abortController.signal,
      })
      if (response.status !== 200) {
        throw new Error(response.statusText)
      }
      const detectedSize = parseInt(response.headers.get('Content-Length') || '0')
      const contentType = (response.headers.get('content-type') || '').toLowerCase()
      let responseBody = response.body
      if (!responseBody) {
        throw new Error('Empty response body')
      }
      if (contentType.includes('application/octet-stream')) { // raw byte chunks
        responseBody = responseBody
          .pipeThrough(new ProgressEmittingByteStreamProcessor(detectedSize, config.progressListener))
      } else if (contentType.includes('application/x-ndjson')) { // base64 encoded json chunks
        responseBody = responseBody
          .pipeThrough(new Utf8DecodingStream())
          .pipeThrough(new NdjsonParseStream<T>())
          .pipeThrough(new Base64TpByteStream<T>(detectedSize, config.converter, config.errorConverter, config.progressListener))
      } else {
        throw new Error(`Unsupported content type: ${contentType || 'unknown'}`)
      }
      const reader = responseBody.getReader()
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }
        await writableStream.write(value)
      }
      return writableStream.close()
    },
  }
}

type Chunk = {
  content?: {
    size?: number
  }
}
class ProgressEmittingByteStreamProcessor extends TransformStream<Uint8Array<ArrayBuffer>, Uint8Array<ArrayBuffer>> {
  private downloadProgress: DownloadProgress = {
    downloaded: 0,
    totalSize: 0,
  }

  constructor(detectedTotalSize: number, progressTracker?: ProgressListener) {
    super({
      transform: (chunk, controller) => {
        this.downloadProgress.downloaded += chunk.length
        controller.enqueue(chunk)
        if (progressTracker) {
          progressTracker(this.downloadProgress)
        }
      },
      flush: (_controller) => {

      },
    })
    this.downloadProgress = {
      downloaded: 0,
      totalSize: detectedTotalSize,
    }
  }
}
/**
 * Base64 parsing TransformStream.
 *
 * Takes a stream of objects of type T, converts them to base64 and emits Uint8Array
 *
 * Propagates errors and tracks progress. Usually first object should contain the total size of the stream. If total size it's known beforehand it can be passed as argument.
 */
class Base64TpByteStream<T> extends TransformStream<T, Uint8Array<ArrayBuffer>> {
  private downloadProgress: DownloadProgress = {
    downloaded: 0,
    totalSize: 0,
  }

  constructor(detectedTotalSize: number, converter: MessageToBase64Converter<T>, errorConverter: ErrorConverter<T>, progressTracker?: ProgressListener) {
    super({
      transform: (chunk, controller) => {
        const error = errorConverter(chunk)
        if (error) {
          return controller.error(error)
        }
        if (this.downloadProgress.totalSize === 0) {
          const d = chunk as Chunk
          this.downloadProgress.totalSize = d.content?.size || 0 // TODO find a way to extract the size in a generic way, Maybe T should extend some DownloadPart type where first part has the size embedded
        }
        const base64Encoded = converter(chunk)
        if (base64Encoded !== null) {
          const chunkBytes = Uint8Array.from(atob(base64Encoded), c => c.charCodeAt(0))
          this.downloadProgress.downloaded += chunkBytes.length
          controller.enqueue(chunkBytes)
        }
        if (progressTracker) {
          progressTracker(this.downloadProgress)
        }
      },
      flush: (_controller) => {

      },
    })
    this.downloadProgress = {
      downloaded: 0,
      totalSize: detectedTotalSize,
    }
  }
}
/**
 * NDJSON parsing TransformStream.
 *
 * Takes a stream of text chunks and emits parsed JSON objects of type T.
 *
 * Uses minimal buffering (only partial line if stream ends mid-line).
 */
class NdjsonParseStream<T> extends TransformStream<string, T> {
  private buffer = ''

  constructor() {
    super({
      transform: (chunk, controller) => {
        this.buffer += chunk

        const lines = this.buffer.split('\n')
        this.buffer = lines.pop() || '' // save incomplete line

        for (const line of lines) {
          const trimmed = line.trim()
          if (trimmed) {
            const parsed: T = JSON.parse(trimmed)
            controller.enqueue(parsed)
          }
        }
      },
      flush: (controller) => {
        const trimmed = this.buffer.trim()
        if (trimmed) {
          const parsed: T = JSON.parse(trimmed)
          controller.enqueue(parsed)
        }
      },
    })
    this.buffer = ''
  }
}
/**
 * Safe UTF-8 decoding TransformStream.
 *
 * Avoids the pitfalls of TextDecoderStream (which may re-emit bytes when
 * multi-byte UTF-8 characters straddle chunk boundaries).
 *
 * Each byte is decoded exactly once and UTF-8 boundaries are strictly enforced.
 */
class Utf8DecodingStream extends TransformStream<Uint8Array<ArrayBuffer>, string> {
  private remainder: Uint8Array<ArrayBuffer> = new Uint8Array()
  private decoder = new TextDecoder('utf-8')

  constructor() {
    super({
      transform: (chunk, controller) => {
        // prepend leftover bytes from last chunk
        const combined = concatUint8(this.remainder, chunk)

        // find the last UTF-8 safe boundary
        const cutoff = findUtf8CharBoundary(combined)

        // safe full characters
        const safe = combined.subarray(0, cutoff)

        // leftover, possibly incomplete UTF-8 bytes
        this.remainder = combined.subarray(cutoff)

        if (safe.length > 0) {
          // decode EXACTLY once
          controller.enqueue(
            this.decoder.decode(safe, { stream: false })
          )
        }
      },
      flush: (controller) => {
        if (this.remainder.length > 0) {
          controller.enqueue(this.decoder.decode(this.remainder))
        }
      },
    })
    this.remainder = new Uint8Array()
  }
}
/**
 * Concatenate two Uint8Arrays using memory copy
 */
function concatUint8(a: Uint8Array<ArrayBuffer>, b: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  if (!a || a.length === 0) return b
  const out = new Uint8Array(a.length + b.length)
  out.set(a, 0)
  out.set(b, a.length)
  return out
}
/**
 * Finds the last valid UTF-8 boundary in a byte array.
 *
 * Ensures multi-byte characters are never decoded as partial sequences.
 * @apiNotes - some history https://www.cl.cam.ac.uk/~mgk25/ucs/utf-8-history.txt
 *
 * | Character | Encoding bytes | High-bit pattern of leading byte |
 * | --------- | -------------- | -------------------------------- |
 * | ASCII     | 1 byte         | `0xxxxxxx`                       |
 * | 2-byte    | 2 bytes        | `110xxxxx`                       |
 * | 3-byte    | 3 bytes        | `1110xxxx`                       |
 * | 4-byte    | 4 bytes        | `11110xxx`                       |
 */
function findUtf8CharBoundary(bytes: Uint8Array<ArrayBuffer>): number {
  let i = bytes.length

  // Skip continuation bytes (10xxxxxx)
  while (i > 0 && (bytes[i - 1] & 0b11000000) === 0b10000000) {
    i--
  }

  const lead = bytes[i - 1]

  if (lead >= 0b11000000) { // the boundry always starts with 0b11
    const expected // how many bytes does a character have
            = lead >= 0xF0
              ? 4
              : lead >= 0xE0
                ? 3
                : 2

    const actual = bytes.length - (i - 1) // how many bytes do we have in this chunk

    /**
     * chunk end: [1110xxxx][10xxxxxx]
     * next chunk:             [10xxxxxx] We only got 2 of 3 required bytes incomplete.
     */
    if (actual < expected) {
      return i - 1 // incomplete char → drop it
    }
  }

  return bytes.length
}
